import type { PropertyFilters } from '~/core/entities/property/filters';

import { filtersAppliedMap } from '~/constants';
import { MixpanelProps } from '~/core/service/mixpanel';
import { isArrayDefinedAndNotEmpty, isGreaterThanZeroValue } from '~~/utils';

export type FilterConditions = {
	[key: string]: boolean | undefined;
};

function getStatusFilterEventValue(
	filters: PropertyFilters
): string | undefined {
	const values: string[] = [];

	if (filters.isExclusive) {
		values.push('exclusive');
	}

	if (filters.hasOpenHouse === true) {
		values.push('open_house');
	}

	return values.length > 0 ? values.join(', ') : undefined;
}

function hasStatusFilterApplied(filters: PropertyFilters): boolean {
	return (filters.isExclusive ?? false) || filters.hasOpenHouse === true;
}

export const useEventDataFilters = () => {
	const route = useRoute();

	const teste = useState<{ lastFilters: PropertyFilters }>(
		'mixpanel-filters-data',
		() => ({
			lastFilters: {
				transactionType: 'sell'
			}
		})
	);

	//This mapping is used to map the filter key to mixpanel filter_applied property on filter_applied event
	const filterMapping = {
		bedrooms: 'rooms',
		isExclusive: 'status',
		hasOpenHouse: 'status',
		minArea: 'area',
		maxArea: 'area',
		minAskingPrice: 'price',
		maxAskingPrice: 'price',
		parkingSpots: 'parking',
		propertyTypes: 'property type',
		regions: 'neighborhoods',
		cities: 'city',
		macroregions: 'region',
		addressSlug: 'address / point of interest',
		text: 'address / point of interest',
		suites: 'suites',
		features: 'attributes',
		minSquareMeterPrice: 'price per sqm',
		maxSquareMeterPrice: 'price per sqm',
		minTax: 'iptu',
		maxTax: 'iptu',
		minCondoFee: 'condo fee',
		maxCondoFee: 'condo fee',
		bathrooms: 'bathrooms',
		agentSlug: 'agent'
	};

	const getNewlyAppliedFiltersString = (
		filters: PropertyFilters | (PropertyFilters & SelectAddressEvent),
		newFilterFields: string[]
	): string => {
		const filtersApplied: string[] = [];

		const keysToIgnore =
			filters.regions && filters.regions.length > 0
				? ['cities', 'macroregions']
				: [];

		newFilterFields?.forEach(key => {
			if (key in filterMapping) {
				if (keysToIgnore.includes(key)) {
					return;
				}
				const mappedFilterName =
					filterMapping[key as keyof typeof filterMapping];
				filtersApplied.push(mappedFilterName);
			}
		});

		if (
			filters &&
			'searchType' in filters &&
			['street', 'point_of_interest'].includes(filters.searchType ?? '')
		) {
			filtersApplied.push(filtersAppliedMap.street);
		}

		return filtersApplied.join(', ');
	};

	const getFiltersAppliedLabels = (
		filters: PropertyFilters
	): FilterConditions => {
		return {
			neighborhoods: isArrayDefinedAndNotEmpty(filters.regions),
			price:
				isGreaterThanZeroValue(filters.minAskingPrice) ||
				isGreaterThanZeroValue(filters.maxAskingPrice),
			area:
				isGreaterThanZeroValue(filters.minArea) ||
				isGreaterThanZeroValue(filters.maxArea),
			'property type': isArrayDefinedAndNotEmpty(filters.propertyTypes),
			rooms: isGreaterThanZeroValue(filters.bedrooms),
			attributes: isArrayDefinedAndNotEmpty(filters.features),
			'price per sqm':
				isGreaterThanZeroValue(filters.minSquareMeterPrice) ||
				isGreaterThanZeroValue(filters.maxSquareMeterPrice),
			status: hasStatusFilterApplied(filters),
			'condo fee':
				isGreaterThanZeroValue(filters.minTax) ||
				isGreaterThanZeroValue(filters.maxTax)
		};
	};

	const getFilterAppliedValues = (
		filters: PropertyFilters | (PropertyFilters & SelectAddressEvent),
		filteredBy: 'quickFilter' | 'moreFilters' | 'searchBar' | 'openFilters',
		newFilterFields: string[]
	) => {
		const filterConditions = getFiltersAppliedLabels(filters);

		const getValueIfDefined = (condition: boolean | undefined, value: any) =>
			condition ? value : undefined;

		const filtersValueResumed = getResumedFilterAppliedValues(
			filters,
			filteredBy,
			newFilterFields
		);

		teste.value.lastFilters = filters;

		return {
			...filtersValueResumed,
			[MixpanelProps.NEIGHBORHOODS_COUNT]: getValueIfDefined(
				filterConditions['neighborhoods'],
				filters.regions?.length
			),
			[MixpanelProps.MIN_PRICE]: getValueIfDefined(
				filterConditions['price'],
				filters.minAskingPrice
			),
			[MixpanelProps.MAX_PRICE]: getValueIfDefined(
				filterConditions['price'],
				filters.maxAskingPrice
			),
			[MixpanelProps.MIN_AREA]: getValueIfDefined(
				filterConditions['area'],
				filters.minArea
			),
			[MixpanelProps.MAX_AREA]: getValueIfDefined(
				filterConditions['area'],
				filters.maxArea
			),
			[MixpanelProps.PROPERTY_TYPE]: filters.propertyTypes?.join(', '),
			[MixpanelProps.BEDROOMS]: getValueIfDefined(
				filterConditions['rooms'],
				filters.bedrooms
			),
			[MixpanelProps.BATHROOMS]: getValueIfDefined(
				isGreaterThanZeroValue(filters.bathrooms),
				filters.bathrooms
			),
			[MixpanelProps.SUITES]: getValueIfDefined(
				isGreaterThanZeroValue(filters.suites),
				filters.suites
			),
			[MixpanelProps.PARKING_SPOTS]: getValueIfDefined(
				isGreaterThanZeroValue(filters.parkingSpots),
				filters.parkingSpots
			),
			[MixpanelProps.ATTRIBUTES]: getValueIfDefined(
				filterConditions['attributes'],
				filters.features?.join(', ')
			),
			[MixpanelProps.MIN_PRICE_PER_SQM]: getValueIfDefined(
				filterConditions['price per sqm'],
				filters.minSquareMeterPrice
			),
			[MixpanelProps.MAX_PRICE_PER_SQM]: getValueIfDefined(
				filterConditions['price per sqm'],
				filters.maxSquareMeterPrice
			),
			[MixpanelProps.STATUS]: getStatusFilterEventValue(filters),
			[MixpanelProps.MIN_IPTU]: getValueIfDefined(
				isGreaterThanZeroValue(filters.minTax),
				filters.minTax
			),
			[MixpanelProps.MAX_IPTU]: getValueIfDefined(
				isGreaterThanZeroValue(filters.maxTax),
				filters.maxTax
			),
			[MixpanelProps.MIN_CONDO_FEE]: getValueIfDefined(
				isGreaterThanZeroValue(filters.minCondoFee),
				filters.minCondoFee
			),
			[MixpanelProps.MAX_CONDO_FEE]: getValueIfDefined(
				isGreaterThanZeroValue(filters.maxCondoFee),
				filters.maxCondoFee
			)
		};
	};

	/**
	 * Slug → {region(city UF), city, neighborhood, address}
	 *
	 * Accepts:
	 * 1) "Rua X, Bairro Y, Cidade Z - UF"
	 * 2) "Rua X, 123, Bairro Y, Cidade Z - UF"
	 */
	const PATTERNS: RegExp[] = [
		/^([^,]+),\s*(?:(\d+),\s*)?([^,]+),\s*([^-]+)-\s*([A-Z]{2})$/,
		/^([^,]+),\s*(?:(\d+),\s*)?([^,]+)\s*-\s*([^,]+),\s*([A-Z]{2})$/
	];

	type Parts = {
		city_name: string;
		neighborhood_name: string;
		address_name: string;
	};

	function parseAddressSlug(slug = ''): Parts | null {
		let m: RegExpMatchArray | null = null;
		for (const re of PATTERNS) {
			m = slug.match(re);
			if (m) break;
		}
		if (!m) return null;

		const [, street, num, neighborhood, city, uf] = m;
		return {
			city_name: city?.trim() || '',
			neighborhood_name: neighborhood?.trim() || '',
			address_name: num ? `${street?.trim()}, ${num}` : street?.trim() || ''
		};
	}

	const getResumedFilterAppliedValues = (
		filters: PropertyFilters | (PropertyFilters & SelectAddressEvent),
		filteredBy: 'quickFilter' | 'moreFilters' | 'searchBar' | 'openFilters',
		newFiltersFields: string[],
		searchTerm?: string
	) => {
		let region = filters.macroregions?.join(', ') || '';
		let city = filters.cities?.join(', ') || '';
		let neighborhood = filters.regions?.join(', ') || '';
		let addressName = '';
		if (filters.addressSlug && filters.addressSlug.includes(',')) {
			const address = parseAddressSlug(filters.addressSlug);
			city = address?.city_name || '';
			neighborhood = address?.neighborhood_name || '';
			addressName = address?.address_name || '';
		}

		return {
			[MixpanelProps.FILTERS_APPLIED]: getNewlyAppliedFiltersString(
				filters,
				newFiltersFields
			),
			[MixpanelProps.CURRENT_PAGE]: route.fullPath,
			[MixpanelProps.TOTAL_RESULTS]: 0,
			[MixpanelProps.FILTERED_BY_OPEN_FILTERS]: filteredBy === 'openFilters',
			[MixpanelProps.FILTERED_BY_SEARCH_BAR]: filteredBy === 'searchBar',
			[MixpanelProps.FILTERED_BY_QUICK_FILTER]: filteredBy === 'quickFilter',
			[MixpanelProps.FILTERED_BY_MORE_FILTERS]: filteredBy === 'moreFilters',
			[MixpanelProps.REGION_NAME]: region,
			[MixpanelProps.CITY_NAME]: city,
			[MixpanelProps.NEIGHBORHOODS_NAME]: neighborhood,
			[MixpanelProps.SEARCH_TERM]: searchTerm,
			[MixpanelProps.ADDRESS_OR_POINT_OF_INTEREST_NAME]: addressName
		};
	};

	function getFilterClearedEventStatus(
		filters: PropertyFilters,
		property_count: number
	) {
		return {
			[MixpanelProps.NEIGHBORHOODS_CLEARED]: isArrayDefinedAndNotEmpty(
				filters.regions
			),
			[MixpanelProps.PRICE_CLEARED]:
				isGreaterThanZeroValue(filters.minAskingPrice) ||
				isGreaterThanZeroValue(filters.maxAskingPrice),
			[MixpanelProps.AREA_CLEARED]:
				isGreaterThanZeroValue(filters.minArea) ||
				isGreaterThanZeroValue(filters.maxArea),
			[MixpanelProps.PROPERTY_TYPE_CLEARED]: isArrayDefinedAndNotEmpty(
				filters.propertyTypes
			),
			[MixpanelProps.ROOMS_CLEARED]: (filters.bedrooms ?? 0) > 0,
			[MixpanelProps.ATTRIBUTES_CLEARED]: isArrayDefinedAndNotEmpty(
				filters.features
			),
			[MixpanelProps.PRICE_PER_SQM_CLEARED]:
				isGreaterThanZeroValue(filters.minSquareMeterPrice) ||
				isGreaterThanZeroValue(filters.maxSquareMeterPrice),
			[MixpanelProps.STATUS_CLEARED]: hasStatusFilterApplied(filters),
			[MixpanelProps.IPTU_CLEARED]:
				isGreaterThanZeroValue(filters.minTax) ||
				isGreaterThanZeroValue(filters.maxTax),
			[MixpanelProps.CONDO_FEE_CLEARED]:
				isGreaterThanZeroValue(filters.minCondoFee) ||
				isGreaterThanZeroValue(filters.maxCondoFee),
			[MixpanelProps.PREVIOUS_RESULTS]: property_count,
			[MixpanelProps.TOTAL_RESULTS]: 0
		};
	}

	return {
		getFilterAppliedValues,
		getFiltersAppliedLabels,
		getFilterClearedEventStatus,
		getResumedFilterAppliedValues
	};
};
